// Laufende Gespräche (Live Calls): ein gemeinsamer Abruf für den Reiter-Zähler und die Live-Seite. Die Telefonie meldet Anrufe nur
// an den Anrufer (Echtzeit `call.*`), deshalb fragt die Oberfläche alle paar Sekunden nach, solange jemand zusieht und das Fenster
// sichtbar ist; eigene Anruf-Ereignisse lösen sofort eine Nachfrage aus.
import { useEffect, useState } from "react";
import { gespraecheApi, type LiveGespraech, type MeetingHeute } from "../api/gespraeche";
import { echtzeit } from "../api/echtzeit";
import { beimNutzerwechsel } from "../daten/sitzung";

export interface LiveStand {
  gespraeche: LiveGespraech[];
  meetings: MeetingHeute[];
  geladen: boolean;
  fehler: unknown;
}

const LEER: LiveStand = { gespraeche: [], meetings: [], geladen: false, fehler: null };
export const LIVE_TAKT_MS = 5000;

let stand: LiveStand = LEER;
const hoerer = new Set<() => void>();
let timer: number | undefined;
let abbruch: AbortController | null = null;
let weg: (() => void) | null = null;

beimNutzerwechsel(() => {
  stand = LEER;
});

function setze(neu: LiveStand): void {
  stand = neu;
  hoerer.forEach((h) => h());
}

async function holen(): Promise<void> {
  abbruch?.abort();
  const ctrl = new AbortController();
  abbruch = ctrl;
  try {
    const r = await gespraecheApi.live(ctrl.signal);
    if (ctrl.signal.aborted) return;
    setze({ gespraeche: r.data, meetings: r.meetings_heute ?? [], geladen: true, fehler: null });
  } catch (e) {
    if ((e as Error).name === "AbortError") return;
    // Der letzte Stand bleibt stehen; ein Netzaussetzer leert die Anzeige nicht
    setze({ ...stand, geladen: true, fehler: e });
  }
}

function takt(): void {
  if (document.visibilityState === "visible") void holen();
}

function starten(): void {
  void holen();
  timer = window.setInterval(takt, LIVE_TAKT_MS);
  document.addEventListener("visibilitychange", takt);
  weg = echtzeit.abonnieren((e) => {
    if (e.type === "call.started" || e.type === "call.updated" || e.type === "call.ended") void holen();
  });
}

function stoppen(): void {
  window.clearInterval(timer);
  timer = undefined;
  document.removeEventListener("visibilitychange", takt);
  weg?.();
  weg = null;
  abbruch?.abort();
}

/** Stand der laufenden Gespräche; der Abruf läuft, solange mindestens eine Komponente ihn benutzt. */
export function useLive(): LiveStand & { neuLaden: () => void } {
  const [, setN] = useState(0);
  useEffect(() => {
    const h = () => setN((n) => n + 1);
    hoerer.add(h);
    if (hoerer.size === 1) starten();
    return () => {
      hoerer.delete(h);
      if (!hoerer.size) stoppen();
    };
  }, []);
  return { ...stand, neuLaden: () => void holen() };
}
